import { ReactNode } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CheckCircleIcon } from "@heroicons/react/24/outline";

import {
  IProductionDashboardAssembly,
  IProductionDashboardBay,
  IProductionDashboardBayVehicle,
  IProductionDashboardSession,
  PartAssemblyType,
  ProductionDashboardAssemblyKind,
} from "../../models/ProductionDashboard";
import { isAwaitingNextStation } from "../../utils/awaitingNextStation";
import classNames from "../../utils/classNames";
import {
  buildFlowLane,
  flowStagedVehicleIds,
  IFlowLaneColumn,
} from "../../utils/flowLaneColumns";
import { formatDurationHms, sessionElapsedSeconds } from "../../utils/sessionElapsed";
import { complexityChipClasses, complexityShort } from "../../utils/vehicleDisplay";
import AssemblyTimer from "./AssemblyTimer";
import OpenVehicleJobButtons from "./OpenVehicleJobButtons";
import Avatar from "../user/Avatar";
import { WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useSignalR } from "../../contexts/SignalRContext";

interface AssemblyFlowProps {
  bays: IProductionDashboardBay[];
  assemblies: IProductionDashboardAssembly[];
  now: DateTime;
  // Vehicles busy with an active triage/depollution timer — hidden from the assembly columns. When
  // the Triage/Depollution stages are part of the flow they reappear in their own stage column.
  busyVehicleIds?: Set<string>;
  triages?: IProductionDashboardSession[];
  depollutions?: IProductionDashboardSession[];
}

function VehicleChip({
  vehicle,
  now,
  progressPercent,
}: {
  vehicle: IProductionDashboardBayVehicle;
  now?: DateTime;
  /** Overrides the bar when the overall figure is not the useful one - see the sub-assembly columns. */
  progressPercent?: number;
}) {
  const { t } = useTranslation("production");
  const { activeJobSessionsByVehicle } = useSignalR();
  const activeJobUsers = activeJobSessionsByVehicle[vehicle.id] ?? [];
  // Finished here but not yet moved on - blue marks it as ready for the next station.
  const awaitingNextStation = isAwaitingNextStation(vehicle);

  return (
    <div
      className={classNames(
        "border rounded-md shadow-sm px-2 py-1.5",
        awaitingNextStation ? "bg-blue-50 border-blue-300" : "bg-white border-gray-200"
      )}
      title={awaitingNextStation ? t("Finished this station - waiting to move on") : undefined}
    >
      <div className="flex items-center justify-between gap-1.5">
        <span className="text-xs font-semibold text-gray-900 truncate">
          {vehicle.vrm || t("No VRM")}
        </span>
        <span
          className={classNames(
            "text-[9px] font-semibold uppercase tracking-wide rounded px-1 py-0.5 shrink-0",
            complexityChipClasses(vehicle.complexity)
          )}
        >
          {complexityShort(vehicle.complexity)}
        </span>
      </div>
      <div className="text-[10px] text-gray-600 truncate">
        {[vehicle.combinedMake, vehicle.combinedModel].filter(Boolean).join(" ")}
      </div>
      {now && (
        <div className="mt-1 flex items-center justify-between gap-1">
          <AssemblyTimer vehicle={vehicle} now={now} size="sm" />
          {vehicle.currentAssemblyPartsTotal > 0 && (
            <span className="text-[10px] font-medium text-gray-500 tabular-nums shrink-0">
              {t("{{removed}}/{{total}} parts", {
                removed: vehicle.currentAssemblyPartsRemoved,
                total: vehicle.currentAssemblyPartsTotal,
              })}
            </span>
          )}
        </div>
      )}
      <div className="mt-1 h-1 w-full rounded bg-gray-100 overflow-hidden">
        <div
          className="h-full bg-emerald-500"
          style={{ width: `${Math.min(100, progressPercent ?? vehicle.percentComplete)}%` }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-1">
        {activeJobUsers.length > 0 ? (
          <div className="flex items-center gap-0.5">
            <WrenchScrewdriverIcon className="w-3 h-3 text-amber-500 shrink-0" />
            <div className="flex">
              {activeJobUsers.map((userId, key) => (
                <Avatar key={key} userId={userId} classes="w-5 h-5 rounded-full [&:not(:first-child)]:-ml-1" textClasses="text-[14px]" />
              ))}
            </div>
          </div>
        ) : (
          <span />
        )}
        <OpenVehicleJobButtons vehicleId={vehicle.id} />
      </div>
    </div>
  );
}

// A triage/depollution session in a stage column. Deliberately plainer than VehicleChip: these
// stages carry no parts progress and no target time, so there is no bar and no cycle-time banding —
// just who is being worked on and for how long.
function SessionChip({
  session,
  now,
  routePrefix,
}: {
  session: IProductionDashboardSession;
  now: DateTime;
  routePrefix: string;
}) {
  const { t } = useTranslation("production");
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/${routePrefix}/${session.id}`)}
      className="bg-white border border-gray-200 rounded-md shadow-sm px-2 py-1.5 cursor-pointer hover:bg-gray-50"
    >
      <div className="text-xs font-semibold text-gray-900 truncate">
        {session.vrm || t("No VRM")}
      </div>
      <div className="text-[10px] text-gray-600 truncate">
        {[session.combinedMake, session.combinedModel].filter(Boolean).join(" ") ||
          session.stockNumber ||
          "-"}
      </div>
      <div className="mt-1 font-mono text-[11px] tabular-nums text-primary-600">
        {formatDurationHms(sessionElapsedSeconds(session, now))}
      </div>
    </div>
  );
}

// The shared column shell: the label/name header plus either the chips or the empty dash.
function FlowColumn({
  name,
  label,
  isActive,
  grow = true,
  children,
}: {
  name: string;
  label: string;
  isActive: boolean;
  grow?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={classNames(
        "min-w-[140px] border rounded p-2",
        grow && "flex-1",
        isActive ? "bg-emerald-50 border-emerald-300" : "bg-gray-50 border-gray-200"
      )}
    >
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            {label}
          </div>
          <div className="text-xs font-semibold text-gray-800 truncate">{name}</div>
        </div>
      </div>
      {isActive ? (
        <div className="flex flex-col gap-1.5">{children}</div>
      ) : (
        <div className="text-[10px] text-gray-400 italic py-2 text-center">—</div>
      )}
    </div>
  );
}

/**
 * A vehicle that has finished the main line sits at 100% overall, so in a sub-assembly column its
 * bar is pinned full and says nothing about the work actually happening. There the bar tracks the
 * sub assembly’s own parts instead, matching the parts count beside it. Vehicles still working
 * the main line keep the overall figure every other column shows.
 */
function subAssemblyProgressPercent(
  vehicle: IProductionDashboardBayVehicle
): number | undefined {
  if (vehicle.percentComplete < 100 || vehicle.currentAssemblyPartsTotal <= 0) return undefined;

  return Math.round(
    (100 * vehicle.currentAssemblyPartsRemoved) / vehicle.currentAssemblyPartsTotal
  );
}

function AssemblyColumn({
  name,
  label,
  vehicles,
  now,
  grow = true,
  isSub = false,
}: {
  name: string;
  label: string;
  vehicles: IProductionDashboardBayVehicle[];
  now: DateTime;
  grow?: boolean;
  isSub?: boolean;
}) {
  const { t } = useTranslation("production");
  const isNonAssembly = name === "Non-Assembly";

  return (
    <FlowColumn
      name={name}
      label={isNonAssembly ? t("Other") : label}
      isActive={vehicles.length > 0}
      grow={grow}
    >
      {vehicles.map((v) => (
        <VehicleChip
          key={v.id}
          vehicle={v}
          now={now}
          progressPercent={isSub ? subAssemblyProgressPercent(v) : undefined}
        />
      ))}
    </FlowColumn>
  );
}

// A stage column holds two kinds of chip. Vehicles on this lane that are at the stage get the
// full vehicle chip - timer running, paused, or finished and blue, exactly like an assembly
// column. Sessions are the fallback for a running timer on a vehicle that isn't on the lane yet
// (not allocated, or not started), where all we have is the session itself.
function StageColumn({
  kind,
  vehicles,
  sessions,
  now,
  grow = true,
}: {
  kind: ProductionDashboardAssemblyKind;
  vehicles: IProductionDashboardBayVehicle[];
  sessions: IProductionDashboardSession[];
  now: DateTime;
  grow?: boolean;
}) {
  const { t } = useTranslation("production");
  const isTriage = kind === ProductionDashboardAssemblyKind.Triage;

  return (
    <FlowColumn
      name={isTriage ? t("Triage") : t("Depollution")}
      label={t("Stage")}
      isActive={vehicles.length > 0 || sessions.length > 0}
      grow={grow}
    >
      {vehicles.map((v) => (
        <VehicleChip key={v.id} vehicle={v} now={now} />
      ))}
      {sessions.map((session) => (
        <SessionChip
          key={session.id}
          session={session}
          now={now}
          routePrefix={isTriage ? "triage" : "depollution"}
        />
      ))}
    </FlowColumn>
  );
}

function BayFlowLane({
  bay,
  assemblies,
  now,
  busyVehicleIds,
  stagedVehicleIds,
  triages,
  depollutions,
}: {
  bay: IProductionDashboardBay;
  assemblies: IProductionDashboardAssembly[];
  now: DateTime;
  busyVehicleIds?: Set<string>;
  /** Vehicles shown at a stage on some lane, so the session rows don't repeat them. */
  stagedVehicleIds: Set<string>;
  triages: IProductionDashboardSession[];
  depollutions: IProductionDashboardSession[];
}) {
  const { t } = useTranslation("production");

  // Placement is shared with the Line TV mode (see flowLaneColumns.ts); this component only
  // decides how each column looks.
  const lane = buildFlowLane(bay, assemblies, triages, depollutions, stagedVehicleIds, busyVehicleIds);

  const renderColumn = (column: IFlowLaneColumn, grow: boolean) => {
    const { assembly, assemblyNumber, vehicles, sessions } = column;
    if (assembly.kind !== ProductionDashboardAssemblyKind.Assembly)
      return (
        <StageColumn
          key={`stage-${assembly.kind}`}
          kind={assembly.kind}
          vehicles={vehicles}
          sessions={sessions}
          now={now}
          grow={grow}
        />
      );

    return (
      <AssemblyColumn
        key={assembly.name}
        name={assembly.name}
        label={t("Assembly {{number}}", { number: assemblyNumber })}
        vehicles={vehicles}
        now={now}
        grow={grow}
        isSub={assembly.type === PartAssemblyType.Sub}
      />
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-800 truncate">{bay.name}</h3>
        {lane.inProgressCount > 0 ? (
          <span className="text-[11px] text-gray-500 shrink-0">
            {t("{{count}} in progress", { count: lane.inProgressCount })}
          </span>
        ) : (
          <span className="text-[11px] text-gray-400 shrink-0 italic">{t("No vehicle")}</span>
        )}
      </div>

      <div className="overflow-x-auto">
        <div className="flex flex-col gap-2 min-w-full">
          {/* Main assemblies flow left to right */}
          <div className="flex gap-2">
            {lane.main.map((column) => renderColumn(column, true))}
          </div>

          {/* Sub assemblies sit in a row underneath, keeping their flow-order numbering */}
          {lane.sub.length > 0 && (
            <div className="flex gap-2">
              {lane.sub.map((column) => renderColumn(column, false))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Completed vehicles (finished every assembly) gathered across all lines/bays.
// Shared so the "Completed Today" section and the KPI card always agree.
export function getCompletedVehicles(
  bays: IProductionDashboardBay[],
  busyVehicleIds?: Set<string>
): IProductionDashboardBayVehicle[] {
  return bays.flatMap((bay) =>
    (bay.currentVehicles ?? []).filter(
      (v) => !busyVehicleIds?.has(v.id) && v.percentComplete >= 100
    )
  );
}

// Vehicles that have finished every assembly (across all lines/bays) are pulled
// out of the individual flow lanes and gathered here in a single shared section.
export function CompletedTodaySection({
  vehicles: completed,
}: {
  vehicles: IProductionDashboardBayVehicle[];
}) {
  const { t } = useTranslation("production");

  return (
    <div className="bg-white border border-gray-200 rounded-md p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <CheckCircleIcon
          className={classNames(
            "h-4 w-4 shrink-0",
            completed.length > 0 ? "text-blue-600" : "text-blue-300"
          )}
        />
        <h3 className="text-sm font-semibold text-gray-800">{t("Completed Today")}</h3>
        <span className="text-[11px] text-gray-500">
          {t("{{count}} across all lines", { count: completed.length })}
        </span>
      </div>
      {completed.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
          {completed.map((v) => (
            <VehicleChip key={v.id} vehicle={v} />
          ))}
        </div>
      ) : (
        <div className="text-[11px] text-gray-400 italic py-2">
          {t("No vehicles completed yet today")}
        </div>
      )}
    </div>
  );
}

export default function AssemblyFlow({
  bays,
  assemblies,
  now,
  busyVehicleIds,
  triages,
  depollutions,
}: AssemblyFlowProps) {
  if (assemblies.length === 0 || bays.length === 0) return null;

  const stagedVehicleIds = flowStagedVehicleIds(bays);

  return (
    <div className="flex flex-col gap-3">
      {bays.map((bay) => (
        <BayFlowLane
          key={bay.id}
          bay={bay}
          assemblies={assemblies}
          now={now}
          busyVehicleIds={busyVehicleIds}
          stagedVehicleIds={stagedVehicleIds}
          triages={triages ?? []}
          depollutions={depollutions ?? []}
        />
      ))}
    </div>
  );
}
